import type { ChangeEvent } from 'react';
import type { RefObject } from 'react';

export interface KioskNameFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly maxLength: number;
  /** The refusal under the control. `undefined` is the resting state — frame *07*. */
  readonly error?: string;
  /**
   * The guidance under the refusal [Operator ruling, 2026-09-17].
   *
   * ⛔ **Not a live region and not part of the alert.** It is standing guidance, so re-announcing it on
   * every refusal would bury the sentence that says what actually happened. It reaches a screen reader
   * through `aria-describedby` instead, which is where a field's description belongs.
   */
  readonly helper?: string;
  /**
   * Further description the SCREEN renders outside this component, by element id — read after the helper.
   * It lives outside so it can sit where its frame draws it, as a sibling of the field [03-us-kad-6p].
   */
  readonly describedBy?: string;
  readonly inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * The naming step's one control [Figma · `Field/Kiosk name` on *07 · Name this kiosk* and
 * *08 · Name already taken*, 1440 · 820 · 375, read live 2026-09-16].
 *
 * ⭐ **It is NOT `components/Field`, and the reason is the ERROR state.** Everything about the resting
 * control is the same in both — 40px tall, `--radius-xs`, a `--border-subtle` hairline, 8px padding, a
 * 16/24 value, a 16/20 semibold label 4px above it — but *08* is the first frame in this app that draws a
 * field in error, and what it draws is not what `Field` renders:
 *
 * | | frame *08* | `components/Field` |
 * | --- | --- | --- |
 * | the control's edge | **2px `--destructive`** (#cf192d) | 1px `--error-border` (#ef4444) |
 * | the message | 14/20 in **`--subtle-foreground`**, behind a danger glyph | 14/20 in `--error`, no glyph |
 *
 * ⛔ **Reconciling `Field` to the frame is not this story's change.** Counted 2026-09-16: **18 error
 * call-sites across four candidate-journey screens** — Application (11), Profile update (3), Fit questions
 * (3), Check-in (1) — reaching `Field`, `Select`, `DateField` and `YesNo`, all four of which render the
 * message the same way. ⛔ **None of those screens' frames draws an error state to arbitrate the change**,
 * so a reconciliation would restyle twelve viewports on the strength of one staff-facing frame. The departure is resolved the other way instead: locally, where the frame that
 * decides it is, and the reconciliation is **reported rather than done**
 * [`design-qa/known-departures.md`].
 *
 * ⭐ **The rule, narrowly, so this is not a licence to fork whenever a shared component is inconvenient:**
 * a component is forked into a feature only when **a frame this feature owns differs from the shared one
 * AND reconciling it would restyle screens that have no frame to arbitrate them.** Both halves, or it is a
 * change to the shared component.
 *
 * ⭐ **The precedent is `features/paycard/ProxyIdField`**, which forks `Field` for a **security control**
 * — paste suppression, no autofill, a value that never leaves the component — and which pointedly does
 * **not** fork error rendering: it takes an `errorId` pointing at the screen's own single `role="alert"`
 * and leaves the message to the screen. ⚠️ **`Keypad` and `PinDots` are NOT precedents and were cited as
 * such in an earlier revision of this file**: neither forks anything — `Keypad` composes the shared
 * `Button` — they are components the candidate journey simply does not have.
 *
 * ⛔ **No `*` marker.** The frame's `Required` node is `visible: false` at all three widths
 * (`I12307:35816;3455:2406`), so nothing is drawn — but `required` is set on the control, because a nameless
 * kiosk defeats the three staff surfaces the name exists for [0118] and the obligation has to reach a screen
 * reader rather than be a glyph a sighted user infers.
 *
 * ⛔ **No counter.** `0/240` is a component slot default AND `visible: false`; the ruled bound is 40
 * characters [[`0224`]; [`0225`]], and it is carried by `maxLength` rather than by a number on screen.
 * ✅ **Re-confirmed by the Operator on 2026-09-17** against 0224 — the limit is 40, the counter is a slot
 * default, and one is not to be added.
 */
export function KioskNameField({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  error,
  helper,
  describedBy,
  inputRef,
}: KioskNameFieldProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  // Both, in reading order, so a screen reader gets what happened and then what to do about it.
  const described = [
    ...(error === undefined ? [] : [`${id}-error`]),
    ...(helper === undefined ? [] : [`${id}-helper`]),
    ...(describedBy === undefined ? [] : [describedBy]),
  ];

  return (
    <div className="flex w-full flex-col gap-1">
      <label className="text-base font-semibold leading-5 text-[var(--foreground)]" htmlFor={id}>
        {label}
      </label>
      <input
        aria-describedby={described.length > 0 ? described.join(' ') : undefined}
        aria-invalid={error === undefined ? undefined : true}
        /*
         * A shared device, so browser autofill must never offer the previous staffer's entry — the same
         * rule `components/Field` carries, and the same reason `name` is absent.
         */
        autoComplete="off"
        /*
         * An inset ring rather than a border, matching every other control in this app: the box is
         * `h-10` so the edge is drawn inside the declared height either way, and the idiom is shared.
         *
         * The placeholder takes `--muted-foreground` because that is the colour the frame draws it in
         * (#585756 on *07*), not the `--subtle-foreground` the candidate journey's fields use.
         */
        className={`kiosk-focusable h-10 w-full rounded-[var(--radius-xs)] bg-[var(--card)] p-2 text-base leading-6 text-[var(--foreground)] ring-inset placeholder:text-[var(--muted-foreground)] ${
          error === undefined
            ? 'ring-1 ring-[var(--border-subtle)]'
            : 'ring-2 ring-[var(--destructive)]'
        }`}
        id={id}
        maxLength={maxLength}
        onChange={handleChange}
        placeholder={placeholder}
        ref={inputRef}
        required
        spellCheck={false}
        type="text"
        value={value}
      />
      {error !== undefined && (
        /*
          `role="alert"`: the message appears with no navigation event to carry it, so without a live region
          a screen-reader user is told nothing at all about a refused name [AC-9].
        */
        <div
          className="flex w-full items-center gap-1 text-sm leading-5 text-[var(--subtle-foreground)]"
          id={`${id}-error`}
          role="alert"
        >
          <DangerIcon />
          <span>{error}</span>
        </div>
      )}
      {helper !== undefined && (
        /*
          `pl-5` aligns it under the message rather than under the glyph: the frame's `Below Form` text box
          starts 20px in — the 16px icon plus the row's 4px gap — so the two sentences share a left edge.
        */
        <p
          className="w-full pl-5 text-sm leading-5 text-[var(--subtle-foreground)]"
          id={`${id}-helper`}
        >
          {helper}
        </p>
      )}
    </div>
  );
}

/**
 * The 16px danger glyph the error row opens with [Figma · `Standard Icon` → `Danger Circle` inside
 * `Below Form` on *08*, `I12307:35892;3455:3979`, exported 2026-09-16].
 *
 * Drawn inline rather than shipped as an asset, for the reason `SearchIcon` and `Spinner` are: the asset
 * rule covers *content*, and this is a system glyph whose one colour is a palette token — an exported SVG
 * would bake `--destructive` into a file that stops following the palette.
 *
 * `aria-hidden`, because the sentence beside it says the same thing in words — the state is never carried by
 * colour or by an icon alone.
 */
function DangerIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-4 shrink-0"
      fill="var(--destructive)"
      viewBox="0 0 16 16"
    >
      <path
        clipRule="evenodd"
        d="M7.99967 2.33331C4.87501 2.33331 2.33301 4.87531 2.33301 7.99998C2.33301 11.1246 4.87501 13.6666 7.99967 13.6666C11.1243 13.6666 13.6663 11.1246 13.6663 7.99998C13.6663 4.87531 11.1243 2.33331 7.99967 2.33331ZM7.99967 14.6666C4.32367 14.6666 1.33301 11.676 1.33301 7.99998C1.33301 4.32398 4.32367 1.33331 7.99967 1.33331C11.6757 1.33331 14.6663 4.32398 14.6663 7.99998C14.6663 11.676 11.6757 14.6666 7.99967 14.6666Z"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M7.99609 8.9153C7.72009 8.9153 7.49609 8.6913 7.49609 8.4153V5.4693C7.49609 5.1933 7.72009 4.9693 7.99609 4.9693C8.27209 4.9693 8.49609 5.1933 8.49609 5.4693V8.4153C8.49609 8.6913 8.27209 8.9153 7.99609 8.9153Z"
        fillRule="evenodd"
      />
      <path
        clipRule="evenodd"
        d="M8.00203 11.1972C7.63336 11.1972 7.33203 10.8992 7.33203 10.5306C7.33203 10.1619 7.62736 9.86389 7.99536 9.86389H8.00203C8.3707 9.86389 8.6687 10.1619 8.6687 10.5306C8.6687 10.8992 8.3707 11.1972 8.00203 11.1972Z"
        fillRule="evenodd"
      />
    </svg>
  );
}
